/* <co-notebook> — a 3D page-flip book, rendered as TWO books that share one flip engine:
   - DESKTOP (.co-notebook__book): the full two-page spread. Each sheet pairs two content pages
     (front = right/odd page, back = left/even page); one flip turns a whole sheet.
   - MOBILE (.co-notebook__m-book, <=749px): the same book at ~2× width, left-justified, so you see the
     LEFT page + a sliver of the right (the rest overflows off-screen). Content lives only on the LEFT
     pages (each sheet's back); the right pages are blank/decorative. So flipping right→left walks the
     content pages 1,2,3,4… each landing on the left. cover binding frames it.
   The flip mechanic is identical — a "context" (a book's leaves + flipped counter) is driven by the
   arrows / drag; the active context is chosen by viewport. Flips are INTERRUPTIBLE: there is no
   global animation lock — each leaf tracks its own flight (ctx.flying), so you can spam the arrows or
   grab a page mid-turn (the drag continues from the page's current angle). While any leaf is flying it
   holds the top z; landing settles z only for leaves NOT still in the air. Concurrent sheets can't
   interpenetrate — they hinge on the same spine at different angles, so plane-splitting orders them
   geometrically. Editor-resilient (AbortController). */
(function () {
  if (customElements.get('co-notebook')) return;

  var MOBILE_MQ = '(max-width: 749px)';

  class CoNotebook extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._ac = new AbortController();
      var sig = this._ac.signal;

      this.desk = this._makeCtx(this.querySelector('.co-notebook__book'), 'data-dragging');
      this.mob = this._makeCtx(this.querySelector('.co-notebook__m-book'), 'data-m-dragging');

      this._layoutCtx(this.desk);
      this._layoutCtx(this.mob);

      this.prevBtn = this.querySelector('.co-flickity-button.previous');
      this.nextBtn = this.querySelector('.co-flickity-button.next');
      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self._flipBack(self._active()); }, { signal: sig });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self._flipForward(self._active()); }, { signal: sig });

      if (this.desk.book) this._initDragPosition(this.desk, sig); // desktop: grab a page (left/right half)
      if (this.mob.book) this._initDragDirection(this.mob, sig);  // mobile: swipe/drag left = forward
      this._updateArrows();
      this._warmImages(sig);

      window.addEventListener('resize', this._onResize.bind(this), { signal: sig });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', function (e) {
          if (self.contains(e.target)) self._selectBlock(e.target);
        }, { signal: sig });
      }
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._rz) clearTimeout(this._rz);
      var self = this;
      [this.desk, this.mob].forEach(function (ctx) {
        if (ctx) ctx.leaves.forEach(function (leaf) { self._cancelFinish(leaf); });
      });
      this._ready = false;
    }

    /* Pre-warm every page image once the notebook nears the viewport. Safari otherwise fetches +
       decodes a leaf's lazy images on that leaf's FIRST reveal — mid-flip — repainting the face while
       it turns (a one-frame content jump); after one full pass everything is cached, which is why the
       jitter "heals" itself. Flip them to eager + decode ahead of the first flip instead. */
    _warmImages(sig) {
      var self = this;
      var warm = function () {
        self.querySelectorAll('img').forEach(function (img) {
          try {
            if (img.loading === 'lazy') img.loading = 'eager';
            if (img.decode) img.decode().catch(function () {});
          } catch (e) {}
        });
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) { io.disconnect(); warm(); return; }
          }
        }, { rootMargin: '600px' });
        io.observe(this);
        sig.addEventListener('abort', function () { io.disconnect(); });
      } else {
        warm();
      }
    }

    _makeCtx(book, dragAttr) {
      var leaves = book ? Array.prototype.slice.call(book.querySelectorAll('.co-notebook__leaf')) : [];
      // gen bumps on editor jumps (_goToCtx) so a live drag from before the jump knows to discard itself
      return { book: book, leaves: leaves, m: leaves.length, flipped: 0, flying: new Set(), dragLeaf: null, gen: 0, dragAttr: dragAttr };
    }
    _isMobile() { return window.matchMedia(MOBILE_MQ).matches; }
    _active() { return this._isMobile() ? this.mob : this.desk; }
    _onResize() {
      if (this._rz) clearTimeout(this._rz);
      var self = this;
      this._rz = setTimeout(function () { self._updateArrows(); }, 150); // arrow bounds differ per book
    }

    /* ---- shared flip mechanic (per context) ---- */
    _layoutCtx(ctx) {
      ctx.leaves.forEach(function (leaf, i) {
        leaf.style.transform = i < ctx.flipped ? 'rotateY(-180deg)' : 'rotateY(0deg)';
      });
      this._setZCtx(ctx);
    }
    /* Settle resting z-order — but never touch a leaf that is still mid-flight (it keeps its top-z
       until its own landing settles it), so one leaf landing can't yank another out from under the
       pointer / mid-animation. */
    _setZCtx(ctx) {
      ctx.leaves.forEach(function (leaf, i) {
        if (ctx.flying.has(leaf)) return;
        leaf.style.zIndex = i < ctx.flipped ? i + 1 : ctx.m - i;
      });
    }
    _updateArrows() {
      var ctx = this._active();
      if (this.prevBtn) this.prevBtn.disabled = ctx.flipped <= 0;
      if (this.nextBtn) this.nextBtn.disabled = ctx.flipped >= ctx.m;
    }
    _flipForward(ctx) {
      // no arrow flips at all while a drag owns a leaf in this book: an opposite-direction arrow
      // mid-drag would move `flipped` under the drag and desync the counter from the geometry
      if (!ctx.book || ctx.flipped >= ctx.m || ctx.dragLeaf) return;
      this._animateLeaf(ctx, ctx.leaves[ctx.flipped], 'rotateY(-180deg)', 1);
    }
    _flipBack(ctx) {
      if (!ctx.book || ctx.flipped <= 0 || ctx.dragLeaf) return;
      this._animateLeaf(ctx, ctx.leaves[ctx.flipped - 1], 'rotateY(0deg)', -1);
    }
    /* Start (or retarget) a leaf's flight. Calling this on an already-flying leaf simply redirects its
       transition from the current mid-air angle — that's what makes arrow-reversals seamless. */
    _animateLeaf(ctx, leaf, toTransform, dir) {
      this._cancelFinish(leaf);
      ctx.flying.add(leaf);
      leaf.style.zIndex = ctx.m + 10;
      void leaf.offsetWidth;
      leaf.style.transform = toTransform;
      ctx.flipped += dir;
      this._updateArrows();
      this._armFinish(ctx, leaf);
    }
    /* Per-leaf landing bookkeeping (transitionend + a timeout fallback for retargets that produce no
       transition). Rearming cancels any previous pending finish for the same leaf. */
    _armFinish(ctx, leaf) {
      var self = this;
      this._cancelFinish(leaf);
      var t = setTimeout(finish, 900);
      function onEnd(e) { if (e.propertyName === 'transform' && e.target === leaf) finish(); }
      function finish() {
        leaf.removeEventListener('transitionend', onEnd);
        clearTimeout(t);
        leaf._coFinishCancel = null;
        ctx.flying.delete(leaf);
        self._setZCtx(ctx);
      }
      leaf._coFinishCancel = function () { leaf.removeEventListener('transitionend', onEnd); clearTimeout(t); leaf._coFinishCancel = null; };
      leaf.addEventListener('transitionend', onEnd);
    }
    _cancelFinish(leaf) {
      if (leaf._coFinishCancel) leaf._coFinishCancel();
    }
    /* Current rotateY of a leaf in [-180, 0], read from the computed matrix (mid-transition this is
       the live interpolated angle). rotateY(θ) serializes with cosθ at m11 and sinθ at m31. */
    _leafAngle(leaf) {
      if (typeof DOMMatrix !== 'function') return 0;
      var t = getComputedStyle(leaf).transform;
      if (!t || t === 'none') return 0;
      var m = new DOMMatrix(t);
      var ang = Math.atan2(m.m31, m.m11) * 180 / Math.PI;
      if (ang > 90) ang -= 360; // +180 rounding of a fully-flipped leaf → -180 (never wrap small +noise)
      return Math.min(Math.max(ang, -180), 0);
    }
    _goToCtx(ctx, target) {
      target = Math.max(0, Math.min(target, ctx.m));
      if (target === ctx.flipped && ctx.flying.size === 0) return;
      var self = this;
      ctx.gen++; // invalidate any live drag from before this jump
      ctx.leaves.forEach(function (leaf) { self._cancelFinish(leaf); leaf.setAttribute('data-co-instant', ''); });
      ctx.flying.clear();
      ctx.dragLeaf = null;
      ctx.flipped = target;
      this._layoutCtx(ctx);
      void this.offsetWidth; // apply the jump with transitions off, then re-enable them
      ctx.leaves.forEach(function (leaf) { leaf.removeAttribute('data-co-instant'); });
      this._updateArrows();
    }

    /* ---- editor: jump to the selected block's spread, in whichever book it lives ---- */
    _selectBlock(target) {
      var inMobile = !!target.closest('.co-notebook__m-stage');
      var ctx = inMobile ? this.mob : this.desk;
      var leaf = target.closest('.co-notebook__leaf');
      if (leaf) {
        var k = parseInt(leaf.getAttribute('data-leaf'), 10) || 0;
        var isBack = !!target.closest('.co-notebook__face--back');
        this._goToCtx(ctx, isBack ? k + 1 : k);
      } else if (target.closest('.co-notebook__base--left')) {
        this._goToCtx(ctx, 0);
      } else if (target.closest('.co-notebook__base--right')) {
        this._goToCtx(ctx, ctx.m); // even page count: the last page rides the right base, shown fully flipped
      }
    }

    /* ---- shared drag core ---- */
    /* Acquire a leaf for pointer control. If it's mid-flight (an interrupted flip), start from its
       CURRENT angle so the page continues from where it is instead of snapping to the raw pointer
       mapping. The transition is killed on THIS leaf only (data-co-instant) — other flying leaves
       keep animating undisturbed. */
    _beginDrag(ctx, leaf, dir, startX, half, pointerId) {
      var p0 = 0;
      if (ctx.flying.has(leaf)) {
        var ang = this._leafAngle(leaf); // [-180, 0]
        p0 = dir === 1 ? -ang / 180 : 1 + ang / 180;
        p0 = Math.min(Math.max(p0, 0), 1);
      }
      this._cancelFinish(leaf);
      ctx.flying.add(leaf);
      ctx.dragLeaf = leaf;
      leaf.style.zIndex = ctx.m + 10;
      leaf.setAttribute('data-co-instant', '');
      this.setAttribute(ctx.dragAttr, ''); // cursor styling only
      var drag = { leaf: leaf, dir: dir, startX: startX, half: half, p0: p0, progress: p0, moved: false, pointerId: pointerId, gen: ctx.gen };
      this._applyDragTransform(drag); // freeze at the current angle (transition now off)
      return drag;
    }
    _applyDragTransform(drag) {
      var p = drag.progress;
      drag.leaf.style.transform = 'rotateY(' + (drag.dir === 1 ? -180 * p : -180 * (1 - p)) + 'deg)';
    }
    _moveDrag(drag, clientX) {
      var delta = drag.dir === 1 ? drag.startX - clientX : clientX - drag.startX;
      var p = Math.min(Math.max(drag.p0 + delta / drag.half, 0), 1);
      if (Math.abs(p - drag.p0) > 0.02) drag.moved = true;
      drag.progress = p;
      this._applyDragTransform(drag);
    }

    /* ---- DESKTOP drag: grab a page by which half you press (right half = forward, left = back) ---- */
    _initDragPosition(ctx, sig) {
      var self = this;
      var book = ctx.book;
      var drag = null;

      book.addEventListener('pointerdown', function (e) {
        if (drag || (e.button != null && e.button !== 0)) return; // one pointer owns a drag at a time
        // Presses that start on a link stay clicks: starting the drag would setPointerCapture,
        // which retargets the eventual click to the book and kills the navigation.
        if (e.target && e.target.closest && e.target.closest('a')) return;
        var rect = book.getBoundingClientRect();
        var onRight = e.clientX - rect.left > rect.width / 2;
        var leaf = null, dir = 0;
        if (onRight && ctx.flipped < ctx.m) { leaf = ctx.leaves[ctx.flipped]; dir = 1; }
        else if (!onRight && ctx.flipped > 0) { leaf = ctx.leaves[ctx.flipped - 1]; dir = -1; }
        if (!leaf) return;
        drag = self._beginDrag(ctx, leaf, dir, e.clientX, rect.width / 2, e.pointerId);
        try { book.setPointerCapture(e.pointerId); } catch (err) {}
      }, { signal: sig });

      book.addEventListener('pointermove', function (e) {
        if (!drag || e.pointerId !== drag.pointerId) return;
        if (drag.gen !== ctx.gen) { drag = null; return; } // an editor jump reset the book under this drag
        self._moveDrag(drag, e.clientX);
      }, { signal: sig });

      this._endDrag(ctx, book, function () { return drag; }, function (v) { drag = v; }, sig);
    }

    /* ---- MOBILE drag: direction decides forward/back (the right page is off-screen) ---- */
    _initDragDirection(ctx, sig) {
      var self = this;
      var book = ctx.book;
      var drag = null;

      book.addEventListener('pointerdown', function (e) {
        if (drag || (e.button != null && e.button !== 0)) return; // one pointer owns a drag at a time
        // Taps on links stay clicks (see the desktop handler): capturing here would retarget
        // the click to the book and kill the navigation.
        if (e.target && e.target.closest && e.target.closest('a')) return;
        drag = { leaf: null, dir: 0, startX: e.clientX, half: book.getBoundingClientRect().width / 2, p0: 0, progress: 0, moved: false, pointerId: e.pointerId, gen: ctx.gen };
        try { book.setPointerCapture(e.pointerId); } catch (err) {}
      }, { signal: sig });

      book.addEventListener('pointermove', function (e) {
        if (!drag || e.pointerId !== drag.pointerId) return;
        if (drag.gen !== ctx.gen) { drag = null; return; } // an editor jump reset the book under this drag
        if (!drag.leaf) {
          var dx = e.clientX - drag.startX;
          if (Math.abs(dx) < 4) return;
          var leaf = null, dir = 0;
          if (dx < 0 && ctx.flipped < ctx.m) { leaf = ctx.leaves[ctx.flipped]; dir = 1; }        // drag left → forward
          else if (dx > 0 && ctx.flipped > 0) { leaf = ctx.leaves[ctx.flipped - 1]; dir = -1; }  // drag right → back
          if (!leaf) return;
          var held = self._beginDrag(ctx, leaf, dir, e.clientX, drag.half, drag.pointerId);
          held.moved = true;
          drag = held;
          return;
        }
        self._moveDrag(drag, e.clientX);
        drag.moved = true;
      }, { signal: sig });

      this._endDrag(ctx, book, function () { return drag; }, function (v) { drag = v; }, sig);
    }

    /* Shared drag release: snap to complete (>0.35) or back, then commit the flip count. */
    _endDrag(ctx, book, getDrag, setDrag, sig) {
      var self = this;
      function endDrag(e) {
        var drag = getDrag();
        if (!drag || e.pointerId !== drag.pointerId) return; // only the owning pointer may release
        setDrag(null);
        self.removeAttribute(ctx.dragAttr);
        try { book.releasePointerCapture(e.pointerId); } catch (err) {}
        if (drag.gen !== ctx.gen) return; // an editor jump already reset the book state
        if (!drag.leaf) return;
        var leaf = drag.leaf;
        ctx.dragLeaf = null;
        leaf.removeAttribute('data-co-instant');
        if (!drag.moved && drag.p0 === 0) {
          // pristine tap on a resting page: nothing changed — settle synchronously (no transition fires)
          self._applyDragTransform(drag);
          ctx.flying.delete(leaf);
          self._setZCtx(ctx);
          return;
        }
        var complete = drag.progress > 0.35;
        var toAngle, applyFlip = 0;
        if (drag.dir === 1) { if (complete) { toAngle = -180; applyFlip = 1; } else { toAngle = 0; } }
        else { if (complete) { toAngle = 0; applyFlip = -1; } else { toAngle = -180; } }
        void leaf.offsetWidth; // recalc so the transition is back ON before the snap target lands
        leaf.style.transform = 'rotateY(' + toAngle + 'deg)';
        ctx.flipped += applyFlip;
        self._updateArrows();
        self._armFinish(ctx, leaf);
      }
      book.addEventListener('pointerup', endDrag, { signal: sig });
      book.addEventListener('pointercancel', endDrag, { signal: sig });
    }
  }

  customElements.define('co-notebook', CoNotebook);
})();
